import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  agentPayoutKey,
  recallEvent,
  rememberEvent,
  subscribeAgentPayouts,
  subscribeResync,
  type AgentPayoutOutcome,
  type AgentPayoutPayload,
} from "@/shared/realtime";
import * as Haptics from "@/shared/platform/haptics";
import { useToast } from "@/shared/ui";
import { payoutNoticeMessage } from "../utils/agentEarnings";
import {
  getAgentEarnings,
  listMyAgentPayouts,
  requestAgentPayout,
  type AgentPayout,
  type RequestPayoutPayload,
} from "../services/agentEarningsService";

/**
 * Clés PROPRES, sous la racine `["agent"]`.
 *
 * Elles descendent de `AGENT_QUERY_KEY` pour qu'une invalidation large emporte
 * aussi les gains, sans qu'on ait besoin de l'inverse : invalider les gains ne
 * doit surtout pas balayer le profil, l'identité et le flux d'offres.
 */
export const AGENT_EARNINGS_QUERY_KEY = ["agent", "earnings"] as const;
export const AGENT_PAYOUTS_QUERY_KEY = ["agent", "payouts"] as const;

/**
 * Les gains de la Main — `GET /agent-earnings`.
 *
 * ## Pas de garde sur `agentId`, et ce n'est pas un oubli
 * Les autres hooks agent attendent que `agentId` soit résolu, parce que leurs
 * routes le portent dans l'URL. Celle-ci non : la Main est identifiée par son
 * jeton. Attendre la résolution afficherait un écran vide pour rien.
 *
 * `staleTime` court : c'est de l'argent, et trois événements peuvent le faire
 * bouger à tout moment.
 */
export function useAgentEarnings() {
  return useQuery({
    queryKey: AGENT_EARNINGS_QUERY_KEY,
    queryFn: getAgentEarnings,
    staleTime: 15_000,
  });
}

/** L'historique des retraits — `GET /agent-payouts/mine`. */
export function useAgentPayouts() {
  return useQuery({
    queryKey: AGENT_PAYOUTS_QUERY_KEY,
    queryFn: listMyAgentPayouts,
    staleTime: 15_000,
  });
}

/**
 * Demander un retrait — `POST /agent-payouts`.
 *
 * Invalide les deux listes : la demande apparaît dans l'historique ET retient
 * déjà son montant sur `solde_disponible`. Ne pas recharger les gains laisserait
 * la Main croire qu'elle peut redemander la même somme.
 */
export function useRequestPayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: RequestPayoutPayload) => requestAgentPayout(payload),
    onSuccess: (payout: AgentPayout) => {
      qc.setQueryData<AgentPayout[]>(AGENT_PAYOUTS_QUERY_KEY, (prev) =>
        prev ? [payout, ...prev.filter((p) => p.id !== payout.id)] : [payout],
      );
      void qc.invalidateQueries({ queryKey: AGENT_EARNINGS_QUERY_KEY });
      void qc.invalidateQueries({ queryKey: AGENT_PAYOUTS_QUERY_KEY });
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
    },
    onError: () => {
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Error);
    },
  });
}

/** Ce que la Main vient d'apprendre sur un retrait. */
export type PayoutOutcomeNotice = {
  outcome: AgentPayoutOutcome;
  payload: AgentPayoutPayload;
  at: number;
};

/**
 * Les trois issues d'un retrait, en direct — et le rechargement qu'elles imposent.
 *
 * ## Ce que le hook fait
 *  1. recharge `GET /agent-earnings` et l'historique : le document l'exige
 *     — « À la réception de l'un des trois, rechargez » — parce que le solde a
 *     changé, dans un sens ou dans l'autre ;
 *  2. rend la dernière issue à l'écran, qui affichera `consequence` TELLE
 *     QUELLE sur un refus ou un échec.
 *
 * ## Pourquoi la phrase est mémorisée
 * `consequence` n'existe que dans l'événement : `GET /agent-payouts/mine` porte
 * le motif (`resolution_note`, `payout_error`), jamais la garantie que les gains
 * restent disponibles. Sans mémoire, un rechargement remplacerait les mots du
 * serveur par notre repli — moins bon, et contraire à la règle du projet.
 */
export function useAgentPayoutUpdates(): PayoutOutcomeNotice | null {
  const qc = useQueryClient();
  const [notice, setNotice] = useState<PayoutOutcomeNotice | null>(null);

  useEffect(() => {
    const reload = () => {
      void qc.invalidateQueries({ queryKey: AGENT_EARNINGS_QUERY_KEY });
      void qc.invalidateQueries({ queryKey: AGENT_PAYOUTS_QUERY_KEY });
    };

    const stop = subscribeAgentPayouts((outcome, payload) => {
      const next: PayoutOutcomeNotice = { outcome, payload, at: Date.now() };
      setNotice(next);
      // Seule la phrase du serveur mérite d'être retenue ; le reste est
      // redonné par les deux routes qu'on vient d'invalider.
      if (payload.payout_id && payload.consequence) {
        rememberEvent(agentPayoutKey(payload.payout_id), next);
      }
      reload();
    });

    // `ready { recovered: false }` : les événements de la coupure sont perdus
    // définitivement. Un retrait a pu aboutir pendant ce temps — on relit.
    const stopResync = subscribeResync(reload);

    return () => {
      stop();
      stopResync();
    };
  }, [qc]);

  return notice;
}

/**
 * La phrase du serveur sur une issue passée, si on l'a retenue.
 *
 * Rendue au rechargement, là où l'événement ne repassera plus. Quand elle
 * manque, l'écran affiche la garantie générale (`PAYOUT_NO_LOSS_NOTICE`) : le
 * document ordonne d'insister, la formulation exacte est secondaire.
 */
export function recallPayoutConsequence(payoutId: string | undefined): string | null {
  if (!payoutId) return null;
  const remembered = recallEvent<PayoutOutcomeNotice>(agentPayoutKey(payoutId));
  const phrase = remembered?.payload?.consequence?.trim();
  return phrase ? phrase : null;
}

/**
 * Les issues de retrait, partout dans l'espace agent — pas seulement sur
 * l'écran des gains.
 *
 * ## Le trou que ceci bouche
 * `useAgentPayoutUpdates` ne vit que tant que son écran est monté. Or une Main
 * passe l'essentiel de sa journée ailleurs : sur l'accueil, ou en course. Un
 * virement qui aboutit — ou qui échoue — pendant ce temps ne déclenchait RIEN :
 * ni le rechargement que le document impose, ni l'affichage de `consequence`,
 * qui n'existe que dans l'événement et ne repasse jamais.
 *
 * Monté une fois dans `AgentShell`, ce hook couvre toute la session : il
 * recharge, il retient la phrase du serveur, et il la MONTRE là où la Main se
 * trouve.
 *
 * ## Pourquoi l'écran des gains s'abonne quand même de son côté
 * Il affiche la même issue en bandeau persistant, là où le toast passe. Deux
 * abonnements donc, ce qui coûte trois écouteurs de socket et une relecture
 * dédoublonnée par react-query — et garde l'écran testable seul, sans son shell.
 */
export function useAgentPayoutNotifier(): void {
  const toast = useToast();
  const notice = useAgentPayoutUpdates();

  useEffect(() => {
    if (!notice) return;
    toast.show({
      type: notice.outcome === "paid" ? "success" : "warning",
      message: payoutNoticeMessage(notice.outcome, notice.payload),
      // Un refus porte un motif ET une garantie : trois secondes ne suffisent
      // pas à lire les deux.
      duration: notice.outcome === "paid" ? 4000 : 7000,
    });
    // `notice.at` change à chaque événement — y compris pour deux issues
    // identiques, qui doivent bien produire deux toasts.
  }, [notice, toast]);
}
